import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { validationErrorsToString } from '../../../utils';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import * as Yup from 'yup';
import { ErrorMessage, FastField, Field, Form, Formik } from 'formik';

// STORE
import { useAppSelector } from '../../../store';

// COMPONENTS
import Button from '../../../components/Button';
import LoadingSpinner from '../../../components/LoadingSpinner';
import SingleSelect from '../../../components/SingleSelect';
import FileUpload from '../../../components/FileUpload';
import ExistingCertification from '../../../components/ExistingCertification';
import DateDayExpiryInput from '../../../components/DateDayExpiryInput';

// ICONS
import { ReactComponent as PlusIcon } from '../../../assets/svg/plus.svg';

// TYPES
import { closeModalType } from '../../../components/GlobalModal';
import { AccountExistingCertificationsCertification, DomainCertificationType } from '../../../api/models';

interface RequestVolunteerModalTypes {
	closeModal?: closeModalType;
	update: () => void;
	organisationId: string;
}

const RequestVolunteerModal: React.FC<RequestVolunteerModalTypes> = ({ closeModal, update, organisationId }) => {
	const states = useAppSelector((state) => state?.lists.states);
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const [currentStep, setCurrentStep] = React.useState<string>('stepOne');

	const [showForm, setShowForm] = React.useState<boolean>(true);

	const stepOneComplete = () => {
		setCurrentStep('stepTwo');
	};

	const goBack = () => {
		setShowForm(false);
	};

	const [existingCertifications, setExistingCertifications] = React.useState<
		AccountExistingCertificationsCertification[]
	>([]);

	const getExistingCertifications = async () => {
		setIsLoading(true);

		const response = await apibridge.getAccountExistingCertifications();

		if (response.isError) {
			setIsLoading(false);
		} else {
			const { result = [] } = response;

			setExistingCertifications(
				result.filter(
					(item: AccountExistingCertificationsCertification) =>
						item.certificationType === DomainCertificationType.WorkingWithChildrenCheck && item.validCertification
				)
			);
			setShowForm(false);
			setIsLoading(false);
		}
	};

	React.useEffect(() => {
		getExistingCertifications();
	}, []);

	//Set initial template
	const certificationTemplate = {
		certificationType: DomainCertificationType.WorkingWithChildrenCheck,
		state: '',
		expiry: '',
		certificationNumber: '',
		attachments: [],
		createAttachments: [],
		existingCertifications: true,
		userAcknowledgement: false,
		existingWorkingWithChildren: false
	};

	//Set Initial Values
	const initalWorkingWithChildrenCheckValues = {
		...certificationTemplate
	};

	// Validation
	const workingWithChildrenCheckValidation = Yup.object().shape({
		state: Yup.string().when('existingCertifications', { is: true, then: Yup.string().required('Required') }),
		expiry: Yup.string().when('existingCertifications', { is: true, then: Yup.string().required('Required') }),
		createAttachments: Yup.array().when('existingCertifications', {
			is: true,
			then: Yup.array().min(1, 'Required').required('Required')
		}),
		certificationNumber: Yup.string().when('existingCertifications', {
			is: true,
			then: Yup.string().required('Required')
		}),
		userAcknowledgement: Yup.boolean().when('existingCertifications', {
			is: true,
			then: Yup.boolean().oneOf([true], 'You must agree to continue').required('Required')
		})
	});

	const submitVolunteerRequest = async (values: any) => {
		setIsLoading(true);

		if (existingCertifications && existingCertifications.length > 0 && !showForm) {
			const response = await apibridge.postAccountVolunteerRequest({ userProfileId: organisationId });

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);
				update();
				closeModal && closeModal();
			}
		} else {
			const responseAddCertification = await apibridge.addCertification({
				certificationType: DomainCertificationType.WorkingWithChildrenCheck,
				state: values.state,
				expiry: values.expiry,
				createAttachments: values.createAttachments,
				certificationNumber: values.certificationNumber,
				userAcknowledgement: values.userAcknowledgement
			});

			if (responseAddCertification.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					responseAddCertification.validationErrors.length
						? validationErrorsToString(responseAddCertification.validationErrors)
						: responseAddCertification.message || 'An error has occurred'
				);
			} else {
				const responsePostAccount = await apibridge.postAccountVolunteerRequest({ userProfileId: organisationId });

				if (responsePostAccount.isError) {
					setIsLoading(false);

					setFormErrorMessage(
						responsePostAccount.validationErrors.length
							? validationErrorsToString(responsePostAccount.validationErrors)
							: responsePostAccount.message || 'An error has occurred'
					);
				} else {
					setIsLoading(false);
					update();
					closeModal && closeModal();
				}
			}
		}
	};

	return (
		<div className="request-status">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<p className="request-text">
									Please provide your Working With Children Check (WWCC) to apply for Parent Volunteer status.
								</p>

								<Formik
									initialValues={initalWorkingWithChildrenCheckValues}
									validationSchema={workingWithChildrenCheckValidation}
									onSubmit={submitVolunteerRequest}
									validateOnMount={true}
									validateOnChange={true}
								>
									{(formik) => {
										const addAnother = () => {
											setShowForm(true);
											formik.setFieldValue('userAcknowledgement', false);
										};

										React.useEffect(() => {
											!showForm && existingCertifications && existingCertifications.length > 0
												? formik.setFieldValue('existingCertifications', false)
												: formik.setFieldValue('existingCertifications', true);
										}, [showForm]);

										React.useEffect(() => {
											formik.values.existingWorkingWithChildren && formik.setFieldValue('userAcknowledgement', true);
										}, [formik.values.existingWorkingWithChildren]);

										React.useEffect(() => {
											// Use setTimeout to ensure validation runs after state updates
											const timer = setTimeout(() => {
												formik.validateForm();
											}, 100);
											return () => clearTimeout(timer);
										}, [formik.values]);

										return (
											<Form translate="yes">
												{!showForm && existingCertifications && existingCertifications.length > 0 ? (
													<>
														<ExistingCertification existingCertifications={existingCertifications} />

														<Button onClick={addAnother} type="button" className="btn-text form-group">
															<PlusIcon className="icon" />
															Add another credential
														</Button>
													</>
												) : (
													<>
														<div className="form-group">
															<label htmlFor={'state'}>State of registration</label>

															<Field
																children={() => {
																	return (
																		<>
																			<SingleSelect
																				name={`state`}
																				options={states}
																				onChange={(val: any) => {
																					formik.setFieldValue(`state`, val);
																					formik.validateForm();
																				}}
																				value={formik.values.state}
																			/>
																		</>
																	);
																}}
															/>

															<ErrorMessage name={`state`}>
																{(error) => <div className="form-validation-error">{error}</div>}
															</ErrorMessage>
														</div>

														<div className="form-group">
															<label htmlFor={`expiry`}>Expiry</label>

															<FastField
																className="form-control"
																name={`expiry`}
																children={() => (
																	<DateDayExpiryInput
																		dateTimeString={formik.values.expiry || ''}
																		onChange={(val: any) => {
																			formik.setFieldValue(`expiry`, val);
																			formik.validateForm();
																		}}
																		excludePast={true}
																	/>
																)}
															/>

															<ErrorMessage name={`expiry`}>
																{(error) => <div className="form-validation-error">{error}</div>}
															</ErrorMessage>
														</div>

														<div className="form-group">
															<label htmlFor={`certificationNumber`}>Registration number</label>

															<FastField
																name={`certificationNumber`}
																className="form-control"
																type="text"
																placeholder=""
																onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
																	formik.handleChange(e);
																	formik.validateForm();
																}}
																value={formik.values.certificationNumber}
															/>

															<ErrorMessage name={`certificationNumber`}>
																{(error) => <div className="form-validation-error">{error}</div>}
															</ErrorMessage>
														</div>

														<div className="form-group">
															<Field
																name="attachments"
																children={() => {
																	return (
																		<>
																			<FileUpload
																				name={`attachments`}
																				placeholder="Choose file..."
																				onChange={(val: any[], files: any[]) => {
																					formik.setFieldValue(`createAttachments`, val);
																					formik.setFieldValue(`attachments`, files);
																					formik.validateForm();
																				}}
																				multiple={true}
																				label="Photo of registration"
																				files={formik.values.attachments}
																			/>
																		</>
																	);
																}}
															/>

															<ErrorMessage name={`createAttachments`}>
																{(error) => <div className="form-validation-error">{error}</div>}
															</ErrorMessage>
														</div>

														<div className="form-group acknowledge-container">
															<label className="form-control-checkbox">
																<Field
																	name="userAcknowledgement"
																	type="checkbox"
																	onChange={() =>
																		formik.setFieldValue(`userAcknowledgement`, !formik.values.userAcknowledgement)
																	}
																	checked={formik.values.userAcknowledgement}
																/>

																<span>
																	I have notified the Working with Children Check authority that I am working or
																	volunteering at this organisation.
																</span>
															</label>

															<ErrorMessage name={`userAcknowledgement`}>
																{(error) => <div className="form-validation-error">{error}</div>}
															</ErrorMessage>
														</div>
													</>
												)}

												<Button
													disabled={isLoading || !formik.isValid}
													type="submit"
													className="btn-primary full-width form-group"
												>
													Submit
												</Button>
												<Button type="button" onClick={closeModal} className="btn btn-secondary full-width">
													Cancel
												</Button>
											</Form>
										);
									}}
								</Formik>
							</>
							// 	) : null}
							// </>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default RequestVolunteerModal;
